import { useState, useEffect, useContext, useRef } from "react";
import {
  FaPlus,
  FaSync,
  FaTrash,
  FaEdit,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa";
import OpeningStockPDF from "../../components/inventory/OpeningStockPDF";
import AuthContext from "../../contexts/AuthContext";
import { getRestaurantProfile } from "../../utils/api";
import {
  getOpeningStock,
  createOpeningStock,
  updateOpeningStock,
  deleteOpeningStock,
} from "../../utils/inventoryApi";

const stores = [{ id: "main", name: "Main Store" }];
const uomOptions = ["kg", "pcs", "liters", "g", "ml", "box", "pack"];

export default function OpeningStock() {
  const { restaurantId } = useContext(AuthContext);
  const [restaurant, setRestaurant] = useState("");
  const [selectedStore, setSelectedStore] = useState("main"); // default to 'main'

  // Ensure selectedStore is always set
  useEffect(() => {
    if (!selectedStore) {
      setSelectedStore("main");
    }
  }, [selectedStore]);
  // Remove openingDate state since we don't need date filtering for opening stock
  const [rows, setRows] = useState([]);
  const [editingRow, setEditingRow] = useState(null); // id of row being edited
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    prodName: "",
    uom: "",
    stockQty: "",
    stockRate: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pdfOpen, setPdfOpen] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    getRestaurantProfile(restaurantId).then((profile) =>
      setRestaurant(profile.name || "My Restaurant")
    );
  }, [restaurantId]);

  // Load data when component mounts or when restaurantId changes
  useEffect(() => {
    if (restaurantId) {
      console.log(
        "Component mounted/restaurantId changed, loading opening stock..."
      );
      handleRefresh();
    }
  }, [restaurantId]);

  // Load all items for selected store
  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    console.log("Loading opening stock for:", {
      restaurantId,
      selectedStore,
    });

    getOpeningStock(restaurantId, selectedStore || "main")
      .then((data) => {
        console.log("Opening stock data received:", data);
        setRows(data || []);
      })
      .catch((error) => {
        console.error("Error loading opening stock:", error);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [restaurantId, selectedStore]);

  // Refresh handler
  function handleRefresh() {
    if (!restaurantId) return;
    setLoading(true);
    console.log("Refreshing opening stock for:", {
      restaurantId,
      selectedStore,
    });

    getOpeningStock(restaurantId, selectedStore || "main")
      .then((data) => {
        console.log("Opening stock refresh data:", data);
        setRows(data || []);
      })
      .catch((error) => {
        console.error("Error refreshing opening stock:", error);
        setRows([]);
      })
      .finally(() => setLoading(false));
  }

  function handleAddRow() {
    setEditingRow("new");
    setForm({ prodName: "", uom: "", stockQty: "", stockRate: "" });
    setError(""); // Clear any previous errors
  }

  function handleEditRow(row) {
    setEditingRow(row.id);
    setForm({
      prodName: row.prodName,
      uom: row.uom,
      stockQty: row.stockQty,
      stockRate: row.stockRate,
    });
    setError(""); // Clear any previous errors
  }

  function handleCancelEdit() {
    setEditingRow(null);
    setForm({ prodName: "", uom: "", stockQty: "", stockRate: "" });
    setError(""); // Clear any previous errors
  }

  function getNextProdId() {
    if (rows.length === 0) return 1001;
    const maxId = Math.max(...rows.map((r) => parseInt(r.prodId, 10) || 1000));
    return maxId + 1;
  }

  // Check if product name already exists (for real-time validation)
  function isProductNameDuplicate(prodName, excludeId = null) {
    if (!prodName) return false;
    return rows.some(
      (row) =>
        row.prodName &&
        row.prodName.toLowerCase() === prodName.toLowerCase() &&
        row.id !== excludeId
    );
  }

  // Check if product ID already exists (for real-time validation)
  function isProductIdDuplicate(prodId, excludeId = null) {
    if (!prodId) return false;
    return rows.some((row) => row.prodId === prodId && row.id !== excludeId);
  }

  async function handleSaveRow() {
    if (!form.prodName || !form.uom || !form.stockQty || !form.stockRate) {
      setError("All fields are required");
      return;
    }

    // Frontend validation for duplicate product name
    if (editingRow === "new") {
      if (isProductNameDuplicate(form.prodName)) {
        setError(`Product "${form.prodName}" already exists in opening stock`);
        return;
      }
    } else {
      if (isProductNameDuplicate(form.prodName, editingRow)) {
        setError(`Product "${form.prodName}" already exists in opening stock`);
        return;
      }
    }

    setError("");
    const stockValue = Number(form.stockQty) * Number(form.stockRate);
    const data = {
      prodName: form.prodName,
      uom: form.uom,
      stockQty: Number(form.stockQty),
      stockRate: Number(form.stockRate),
      stockValue,
      storeId: selectedStore,
    };

    try {
      if (editingRow === "new") {
        // prodId: unique 4-digit integer
        data.prodId = getNextProdId().toString();
        console.log("Creating new opening stock item:", data);
        const res = await createOpeningStock(restaurantId, data);
        console.log("Created with ID:", res.id);
        // Refresh the data instead of manually updating state
        await handleRefresh();
      } else {
        console.log("Updating opening stock item:", editingRow, data);
        await updateOpeningStock(restaurantId, editingRow, data);
        // Refresh the data instead of manually updating state
        await handleRefresh();
      }
      setEditingRow(null);
      setForm({ prodName: "", uom: "", stockQty: "", stockRate: "" });
      setSuccess("Item saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error saving opening stock:", error);
      // Handle specific validation errors from backend
      if (error.response && error.response.data && error.response.data.error) {
        setError(error.response.data.error);
      } else {
        setError("Failed to save item. Please try again.");
      }
    }
  }

  async function handleDeleteRow(id) {
    if (!window.confirm("Delete this row?")) return;

    try {
      console.log("Deleting opening stock item:", id);
      await deleteOpeningStock(restaurantId, id);
      // Refresh the data instead of manually updating state
      await handleRefresh();
    } catch (error) {
      console.error("Error deleting opening stock:", error);
      setError("Failed to delete item. Please try again.");
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Clear error and success when user starts typing
    if (error) {
      setError("");
    }
    if (success) {
      setSuccess("");
    }
  }

  function handleExportPDF() {
    setPdfOpen(true);
  }

  // Function to add sample data for testing
  async function addSampleData() {
    const sampleItems = [
      {
        prodName: "Rice",
        uom: "kg",
        stockQty: 50,
        stockRate: 45,
        stockValue: 2250,
        storeId: selectedStore,
        prodId: "1001",
      },
      {
        prodName: "Cooking Oil",
        uom: "liters",
        stockQty: 20,
        stockRate: 120,
        stockValue: 2400,
        storeId: selectedStore,
        prodId: "1002",
      },
      {
        prodName: "Onions",
        uom: "kg",
        stockQty: 30,
        stockRate: 25,
        stockValue: 750,
        storeId: selectedStore,
        prodId: "1003",
      },
    ];

    try {
      for (const item of sampleItems) {
        await createOpeningStock(restaurantId, item);
      }
      setSuccess("Sample data added successfully!");
      setTimeout(() => setSuccess(""), 3000);
      await handleRefresh();
    } catch (error) {
      console.error("Error adding sample data:", error);
      setError("Failed to add sample data");
    }
  }

  // Get items to display (first 10 or all based on showAllItems)
  const itemsToDisplay = showAllItems ? rows : rows.slice(0, 10);
  const hasMoreItems = rows.length > 10;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-3 sm:p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white text-center sm:text-left">
          Opening Stock
        </h2>
        <div className="flex gap-2 sm:gap-3 justify-center sm:justify-end">
          <button
            className="rounded-full bg-blue-100 hover:bg-blue-200 p-2"
            onClick={handleAddRow}
          >
            <FaPlus className="text-blue-600 text-lg sm:text-xl" />
          </button>
          <button
            className="rounded-full bg-amber-100 hover:bg-amber-200 p-2"
            onClick={handleRefresh}
          >
            <FaSync className="text-amber-700 text-lg sm:text-xl" />
          </button>
          <button
            className="rounded-full bg-red-100 hover:bg-red-200 p-2"
            onClick={handleExportPDF}
          >
            <FaFilePdf className="text-red-700 text-lg sm:text-xl" />
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 items-center mb-4">
        <select
          className="border border-gray-300 dark:border-slate-600 rounded px-3 py-2 w-full sm:min-w-[220px] bg-white dark:bg-slate-700 text-gray-800 dark:text-white text-sm"
          value={restaurant}
          disabled
        >
          <option>{restaurant}</option>
        </select>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-gray-700 dark:text-gray-300 text-sm">
            CC Store
          </span>
          <select
            className="border border-gray-300 dark:border-slate-600 rounded px-3 py-2 w-full sm:min-w-[180px] bg-white dark:bg-slate-700 text-gray-800 dark:text-white text-sm"
            value={selectedStore}
            onChange={(e) => {
              setSelectedStore(e.target.value);
              // Refresh data when store changes
              setTimeout(() => handleRefresh(), 100);
            }}
          >
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Info */}
      <div className="mb-4 p-3 sm:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="text-center">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-600 dark:text-blue-400">
              {rows.length}
            </div>
            <div className="text-xs sm:text-sm text-blue-600 dark:text-blue-400">
              Total Items
            </div>
          </div>
          <div className="text-center">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold text-green-600 dark:text-green-400">
              ₹
              {rows
                .reduce((sum, row) => sum + (row.stockValue || 0), 0)
                .toLocaleString()}
            </div>
            <div className="text-xs sm:text-sm text-green-600 dark:text-green-400">
              Total Value
            </div>
          </div>
          <div className="text-center">
            <div className="text-base sm:text-lg lg:text-2xl font-bold text-purple-600 dark:text-purple-400">
              Main Store
            </div>
            <div className="text-xs sm:text-sm text-purple-600 dark:text-purple-400">
              Current Store
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
          <thead className="bg-gray-200 dark:bg-slate-700">
            <tr>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                SNo
              </th>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                Prod Id
              </th>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                Prod Name
              </th>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                UOM
              </th>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                Stock Qty
              </th>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                Stock Rate
              </th>
              <th className="px-2 sm:px-4 py-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200">
                Stock Value
              </th>
              <th className="px-2 sm:px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
            {loading ? (
              <tr>
                <td
                  colSpan={8}
                  className="text-center py-6 sm:py-8 text-gray-400 dark:text-gray-500 text-sm sm:text-base"
                >
                  Loading...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="text-center py-6 sm:py-8 text-gray-400 dark:text-gray-500 text-sm sm:text-base"
                >
                  No opening stock items found. Click the + button to add items.
                </td>
              </tr>
            ) : (
              <>
                {editingRow === "new" && (
                  <tr>
                    <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                      {rows.length + 1}
                    </td>
                    <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                      {getNextProdId()}
                    </td>
                    <td className="px-2 sm:px-4 py-2">
                      <input
                        name="prodName"
                        value={form.prodName}
                        onChange={handleInputChange}
                        className={`border rounded px-1 sm:px-2 py-1 w-20 sm:w-24 lg:w-32 text-xs sm:text-sm ${
                          isProductNameDuplicate(form.prodName)
                            ? "border-red-500 bg-red-50"
                            : ""
                        }`}
                        placeholder="Product name"
                      />
                    </td>
                    <td className="px-2 sm:px-4 py-2">
                      <select
                        name="uom"
                        value={form.uom}
                        onChange={handleInputChange}
                        className="border rounded px-1 sm:px-2 py-1 text-xs sm:text-sm"
                      >
                        <option value="">Select</option>
                        {uomOptions.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 sm:px-4 py-2">
                      <input
                        name="stockQty"
                        type="number"
                        value={form.stockQty}
                        onChange={handleInputChange}
                        className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                        placeholder="Qty"
                      />
                    </td>
                    <td className="px-2 sm:px-4 py-2">
                      <input
                        name="stockRate"
                        type="number"
                        value={form.stockRate}
                        onChange={handleInputChange}
                        className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                        placeholder="Rate"
                      />
                    </td>
                    <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                      {form.stockQty && form.stockRate
                        ? Number(form.stockQty) * Number(form.stockRate)
                        : ""}
                    </td>
                    <td className="px-2 sm:px-4 py-2">
                      <button
                        className="text-green-600 mr-1 sm:mr-2 p-1"
                        onClick={handleSaveRow}
                      >
                        <FaCheck className="text-sm sm:text-base" />
                      </button>
                      <button
                        className="text-gray-400 p-1"
                        onClick={handleCancelEdit}
                      >
                        <FaTimes className="text-sm sm:text-base" />
                      </button>
                    </td>
                  </tr>
                )}
                {itemsToDisplay.map((row, idx) =>
                  editingRow === row.id ? (
                    <tr key={row.id}>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {idx + 1}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {row.prodId}
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <input
                          name="prodName"
                          value={form.prodName}
                          onChange={handleInputChange}
                          className={`border rounded px-1 sm:px-2 py-1 w-20 sm:w-24 lg:w-32 text-xs sm:text-sm ${
                            isProductNameDuplicate(form.prodName, row.id)
                              ? "border-red-500 bg-red-50"
                              : ""
                          }`}
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <select
                          name="uom"
                          value={form.uom}
                          onChange={handleInputChange}
                          className="border rounded px-1 sm:px-2 py-1 text-xs sm:text-sm"
                        >
                          <option value="">Select</option>
                          {uomOptions.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <input
                          name="stockQty"
                          type="number"
                          value={form.stockQty}
                          onChange={handleInputChange}
                          className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <input
                          name="stockRate"
                          type="number"
                          value={form.stockRate}
                          onChange={handleInputChange}
                          className="border rounded px-1 sm:px-2 py-1 w-12 sm:w-16 lg:w-20 text-xs sm:text-sm"
                        />
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {form.stockQty && form.stockRate
                          ? Number(form.stockQty) * Number(form.stockRate)
                          : ""}
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <button
                          className="text-green-600 mr-1 sm:mr-2 p-1"
                          onClick={handleSaveRow}
                        >
                          <FaCheck className="text-sm sm:text-base" />
                        </button>
                        <button
                          className="text-gray-400 p-1"
                          onClick={handleCancelEdit}
                        >
                          <FaTimes className="text-sm sm:text-base" />
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={row.id}>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {idx + 1}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {row.prodId}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {row.prodName}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {row.uom}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {row.stockQty}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {row.stockRate}
                      </td>
                      <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">
                        {row.stockValue}
                      </td>
                      <td className="px-2 sm:px-4 py-2">
                        <button
                          className="text-blue-600 mr-1 sm:mr-2 p-1"
                          onClick={() => handleEditRow(row)}
                        >
                          <FaEdit className="text-sm sm:text-base" />
                        </button>
                        <button
                          className="text-red-600 p-1"
                          onClick={() => handleDeleteRow(row.id)}
                        >
                          <FaTrash className="text-sm sm:text-base" />
                        </button>
                      </td>
                    </tr>
                  )
                )}
                {hasMoreItems && (
                  <tr>
                    <td colSpan={8} className="text-center py-2">
                      <button
                        className="text-blue-600 hover:underline text-sm sm:text-base"
                        onClick={() => setShowAllItems(!showAllItems)}
                      >
                        {showAllItems ? "Read Less" : "Read More"}
                      </button>
                    </td>
                  </tr>
                )}
              </>
            )}
            {error && (
              <tr>
                <td
                  colSpan={8}
                  className="text-center text-red-500 py-2 text-sm sm:text-base"
                >
                  {error}
                </td>
              </tr>
            )}
            {success && (
              <tr>
                <td
                  colSpan={8}
                  className="text-center text-green-500 py-2 text-sm sm:text-base"
                >
                  {success}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <OpeningStockPDF
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        rows={rows}
      />
    </div>
  );
}
